import React, { useEffect, useState } from "react";
import axios from "axios";
import { History, Loader2, X, ArrowRight, Trash2, UserCog, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Map admin_actions row → friendly summary line + icon. Keeps the
 *  presentation logic local to this widget so the backend can keep
 *  shipping new action types without a UI bump. */
function actionDescriptor(row) {
  const a = row?.action || "";
  const d = row?.details || {};
  if (a === "cleanup_legacy_margaret") {
    const total =
      (row.deleted?.pa_jobs || 0) +
      (row.deleted?.audit_records || 0) +
      (row.deleted?.intake_drafts || 0);
    return {
      Icon: Trash2,
      label: "Sweep legacy",
      summary: `Removed ${total} record${total === 1 ? "" : "s"}`,
      tone: "text-[#A42A2A] bg-[#FBECEC] border-[#E6B8B8]",
    };
  }
  if (a === "reassign_draft") {
    return {
      Icon: UserCog,
      label: "Reassign",
      summary: `${d.previous_rn || "(unassigned)"} → ${d.new_rn || "—"}`,
      tone: "text-[#0A3D79] bg-[#EEF5FC] border-[#B7D1EA]",
    };
  }
  if (a === "reconcile_orphan") {
    return {
      Icon: Link2,
      label: "Reconcile",
      summary: d.merged_from
        ? `Merged into ${d.pipeline_id} (absorbed ${d.merged_from})`
        : `Linked to ${d.pipeline_id}`,
      tone: "text-[#0E6B3F] bg-[#E9F5EE] border-[#B5D9C3]",
    };
  }
  // Fallback for unknown action types — never break the UI just because the
  // backend shipped a new entry.
  return {
    Icon: History,
    label: a || "action",
    summary: JSON.stringify(d).slice(0, 80),
    tone: "text-[#3A5275] bg-[#F1F5F9] border-[#D6E3EF]",
  };
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

const FILTER_OPTIONS = [
  { value: "", label: "All", tone: "border-[#0A3D79] bg-[#0A3D79] text-white" },
  { value: "cleanup_legacy_margaret", label: "Cleanup", tone: "border-[#A42A2A] bg-[#A42A2A] text-white" },
  { value: "reassign_draft", label: "Reassign", tone: "border-[#0A3D79] bg-[#0A3D79] text-white" },
  { value: "reconcile_orphan", label: "Reconcile", tone: "border-[#0E6B3F] bg-[#0E6B3F] text-white" },
];

/**
 * Slide-down popover triggered by the "Activity" header button. Shows the
 * last N admin_actions newest-first across all destructive ops (cleanup,
 * reassign, reconcile). Filter pills narrow by action type. Click outside
 * to close. Refreshes when re-opened.
 */
export default function AdminActivityWidget({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) {
      setRows([]);
      setErr("");
      setFilter("");
      return;
    }
  }, [open]);

  // Re-fetch whenever filter changes (or widget reopens with a fresh "")
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr("");
    const params = { limit: 15 };
    if (filter) params.action = filter;
    axios
      .get(`${API}/admin/actions`, { params })
      .then((res) => setRows(res.data?.actions || []))
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not load activity."))
      .finally(() => setLoading(false));
  }, [open, filter]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40"
      data-testid="admin-activity-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="absolute top-[64px] right-4 sm:right-6 w-[min(96vw,420px)] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-[#D6E3EF] flex flex-col"
        data-testid="admin-activity-widget"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#E5EEF5]">
          <div className="flex items-center gap-1.5">
            <History className="w-4 h-4 text-[#0A3D79]" />
            <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[#0A3D79]">
              Recent admin activity
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#3A5275] hover:text-[#0B1E3A]"
            data-testid="admin-activity-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Filter pills — narrow by destructive-op type. Backend supports
            ?action= so this is a single-call refetch, not client filtering. */}
        <div
          className="flex items-center gap-1 px-3 py-2 border-b border-[#E5EEF5] bg-[#F7FAFD]"
          data-testid="admin-activity-filters"
        >
          {FILTER_OPTIONS.map((opt) => {
            const active = filter === opt.value;
            return (
              <button
                key={opt.value || "all"}
                type="button"
                onClick={() => setFilter(opt.value)}
                disabled={loading}
                className={[
                  "text-[10.5px] font-semibold px-2 py-0.5 rounded-full border transition-colors",
                  active
                    ? opt.tone
                    : "border-[#D6E3EF] bg-white text-[#3A5275] hover:border-[#0A3D79] hover:text-[#0A3D79]",
                  loading ? "opacity-60 cursor-wait" : "cursor-pointer",
                ].join(" ")}
                data-testid={`admin-activity-filter-${opt.value || "all"}`}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div
              className="flex items-center gap-2 text-[12.5px] text-[#3A5275] py-6 justify-center"
              data-testid="admin-activity-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" /> Loading recent activity…
            </div>
          )}
          {!loading && err && (
            <div
              className="text-[12.5px] text-[#A42A2A] px-3 py-2 rounded-lg bg-[#FBECEC] border border-[#E6B8B8]"
              data-testid="admin-activity-error"
            >
              {err}
            </div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div
              className="text-[12.5px] text-[#3A5275] py-6 text-center"
              data-testid="admin-activity-empty"
            >
              <History className="w-7 h-7 mx-auto text-[#8FB6D9] mb-2" />
              {filter
                ? `No "${FILTER_OPTIONS.find((o) => o.value === filter)?.label}" actions recorded yet.`
                : "No recent destructive operations."}
              <div className="text-[11px] mt-1">
                {filter
                  ? "Try All to see other action types."
                  : "Cleanups, reassigns, and reconciles will appear here."}
              </div>
            </div>
          )}
          {!loading && !err && rows.length > 0 && (
            <ul className="space-y-2" data-testid="admin-activity-list">
              {rows.map((r, idx) => {
                const desc = actionDescriptor(r);
                const Icon = desc.Icon;
                return (
                  <li
                    key={r.action_id || idx}
                    className="rounded-lg border border-[#D6E3EF] bg-white px-3 py-2"
                    data-testid={`admin-activity-row-${idx}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border ${desc.tone}`}>
                        <Icon className="w-2.5 h-2.5" /> {desc.label}
                      </span>
                      <span className="text-[10.5px] text-[#3A5275] ml-auto">
                        {fmtTs(r.timestamp)}
                      </span>
                    </div>
                    <div className="text-[12px] text-[#0B1E3A] mt-1.5 break-words flex items-center gap-1">
                      {desc.summary || "—"}
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-[11px] text-[#3A5275]">
                      <ArrowRight className="w-3 h-3 text-[#8FB6D9]" />
                      <span className="font-mono truncate">{r.actor || "unknown"}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
